/**
 * Shared layout pieces for the EPIC-111 D4 team rows: every column of a row
 * (heat map, zone grid, thirds pitch) uses the SAME pitch box classes and the
 * SAME crest + "Dirección de ataque" caption, so the three pitch boxes get
 * equal width, height and top, and the captions share a top, by construction
 * (equal-width columns + one aspect ratio + fixed-height label row).
 */
import type { FC } from "react";
import TeamLogo, { type TeamLogoProps } from "../../../../components/TeamLogo";
import AttackDirectionCaption from "../atoms/AttackDirectionCaption";
import type { TeamSide } from "../../utils/teamControlMap";
import { PITCH_WIDTH, PITCH_HEIGHT } from "../../utils/heatMapZones";

/** Pitch drawing box: fills the column, one shared aspect ratio. */
export const ROW_PITCH_BOX_CLASS =
  "relative w-full rounded-lg border border-slate-700 bg-green-800 overflow-hidden";

export const ROW_PITCH_ASPECT = `${PITCH_WIDTH + 4} / ${PITCH_HEIGHT + 4}`;

/** Fixed-height label row above each pitch (same height in every column). */
export const ROW_LABEL_CLASS =
  "flex h-6 items-center justify-center text-center text-xs font-semibold uppercase tracking-wider text-slate-200";

interface CrestDirectionCaptionProps {
  team: TeamLogoProps["team"];
  side: TeamSide;
  t: (key: string, fallback?: string) => string;
  rotated?: boolean;
  /** Base testid for the arrow caption (`${testId}-arrow`). */
  captionTestId?: string;
  /** Testid of the crest wrapper. */
  crestTestId?: string;
}

/** Small crest + "→ Dirección de ataque" caption shared by the three pitches. */
export const CrestDirectionCaption: FC<CrestDirectionCaptionProps> = ({
  team,
  side,
  t,
  rotated = false,
  captionTestId,
  crestTestId,
}) => (
  <div
    className="flex min-h-6 w-full flex-wrap items-center justify-center gap-x-1.5 [&_div]:text-sm"
    data-testid={crestTestId}
  >
    <TeamLogo team={team} size="xs" bare />
    <AttackDirectionCaption
      side={side}
      t={t}
      reversed={rotated}
      data-testid={captionTestId}
    />
  </div>
);
